import PropTypes from 'prop-types';
import ResourceDto from '../../../dtos/Resources/ResourceDto';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
    Autocomplete,
    CardMedia,
    Chip,
    createFilterOptions,
    FormLabel,
    GlobalStyles,
    Grid,
    TextField
} from '@mui/material';
import ResourceTopicDto from '../../../dtos/Resources/ResourceTopicDto';
import React, { useEffect, useState } from 'react';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ResourceApiService from '../../../api/ResourceApiService';
import * as resourceConstants from '../../../constants/resourceConstants';
import FileUploadModal from '../FileUploadModal';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    AutoLink,
    BlockQuote,
    Bold,
    ClassicEditor,
    Essentials,
    FontFamily,
    FontSize,
    Heading,
    Indent,
    Italic,
    Link,
    LinkEditing,
    LinkUI,
    List,
    MediaEmbed,
    Mention,
    Paragraph,
    Table,
    Undo
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import ConfirmDeleteDialog from '../ConfirmDelete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const ResourceTitle = 'title';
const ResourceDescription = 'description';

const inputGlobalStyles = (
    <GlobalStyles
        styles={{
            ".ck.ck-balloon-panel": {
                zIndex: "1400 !important", // Put a higher value that your MUI Dialog (generaly 1300)
            },
        }}
    />
);

export default function EditResourceModal(props) {
    const { resource, setResource, open, types, topics, selectableOrgs, cancel, resourceApiService, setSnackbarProperties } = props;

    const[showAttachmentModal, setShowAttachmentModal] = useState(false);
    const[attachmentType, setAttachmentType] = useState('');
    const[selectedTopics, setSelectedTopics] = useState([]);
    const[resourceTypeInputValue, setResourceTypeInputValue] = useState('');
    const[resourceTitleErrorMessage, setResourceTitleErrorMessage] = useState('');
    const[resourceImageErrorMessage, setResourceImageErrorMessage] = useState('');
    const[showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const[isPublic, setIsPublic] = useState(false);
    const[selectedOrgs, setSelectedOrgs] = useState([]);

    function changeResourceType(newValue) {
        const resourceCopy = {...resource};

        if (newValue === null || newValue === undefined || newValue === '') {
            resourceCopy.resourceType = '';
        } else {
            resourceCopy.resourceType = newValue.replace('Add "', '').replaceAll('"', '');
        }
        setResource(resourceCopy);
    }

    async function handleFileUpload (event) {
        if (event) {
            const attachmentResponse = await resourceApiService.addResourceAttachment(event);
            if(attachmentResponse.success) {
                const resourceCopy = {...resource};
                if(attachmentType === resourceConstants.ImageAttachment) {
                    resourceCopy.resourceImage = attachmentResponse.data;
                } else if (attachmentType === resourceConstants.FileAttachment) {
                    resourceCopy.resourceAttachment = attachmentResponse.data;
                }
                setResource(resourceCopy);
            }
        }

        setShowAttachmentModal(false);
    }

    function updateFieldByName(event, fieldName) {
        const resourceCopy = {...resource};

        switch (fieldName) {
        case ResourceTitle:
            resourceCopy.rsTitle = event.target.value;
            break;
        case ResourceDescription:
            resourceCopy.rsShortDescription = event.target.value;
            break;
        }

        setResource(resourceCopy);
    }

    function updateResourceContent(data) {
        const resourceCopy = {...resource};

        resourceCopy.rsContent = data;
        setResource(resourceCopy);
    }

    async function saveResource() {
        const valid = validate();

        if(valid) {
            if(resource.rsId > 0) {
                const update = await resourceApiService.updateResource(resource);
                if(update.success) {
                    setSnackbarProperties('Successfully updated resource');
                    cancel();
                } else {
                    setSnackbarProperties('Error updating resource: ' + update.message, 'error');
                }
            } else {
                const addResource = await resourceApiService.addResource(resource);
                if(addResource.success) {
                    setSnackbarProperties('Successfully added resource');
                    setResource(null);
                    cancel();
                } else {
                    setSnackbarProperties('Error adding resource: ' + addResource.message, 'error');
                }
            }
        }
    }

    function changeResourceTopics(newValues) {
        const value = newValues.map((value) => {
            const foundTopic = topics.find((topic) => topic.name.toLowerCase() === value.toLowerCase());
            if(foundTopic) {
                return foundTopic;
            } else {
                return new ResourceTopicDto(0, value.replace('Add "', '').replaceAll('"', ''));
            }
        });

        const resourceCopy = {...resource};

        resourceCopy.resourceTopics = value;
        setResource(resourceCopy);
    }

    function changeAllowedOrgs(newValues) {
        const value = newValues
            .map((value) => selectableOrgs.find((org) => org.ogId === value.ogId))
            .filter(Boolean);

        const resourceCopy = {...resource};

        resourceCopy.ctOrgs = value;
        setSelectedOrgs(value);
        setResource(resourceCopy);
    }

    function changePublicVisibility() {
        const resourceCopy = {...resource};
        resourceCopy.isPublic = !isPublic;
        setResource(resourceCopy);
        setIsPublic(!isPublic);
    }

    function parseResourceTopicsForSelect() {
        let parsedSelectedTopics = [];

        resource?.resourceTopics?.forEach((value) => {

            parsedSelectedTopics.push(value.name);
        });

        return parsedSelectedTopics;
    }

    function validate() {
        let isValid = true;
        let titleError = '';
        let imageError = '';


        if(!resource.rsTitle) {
            isValid = false;
            titleError = 'Title is required';
        }
        if(!resource.resourceImage) {
            isValid = false;
            imageError = 'Image is required';
        }

        setResourceTitleErrorMessage(titleError);
        setResourceImageErrorMessage(imageError);

        if(titleError || imageError) {
            setSnackbarProperties(`Not able to save resource: ${titleError}${titleError && imageError ? ', ' : ''}${imageError}`, 'error');
        }

        return isValid;
    }

    async function deleteAttachment(confirmed) {
        if(confirmed) {
            const response = await resourceApiService.deleteResourceAttachment(resource.resourceAttachment.id);
            if(response && response.success) {
                const resourceCopy = {...resource};
                resourceCopy.resourceAttachment = null;
                setResource(resourceCopy);
            } else {
                setSnackbarProperties('Error deleting attachment: ' + response.message, 'error');
            }
        }
        setShowConfirmDeleteModal(false);
    }

    useEffect(() => {
        if(open && resource) {
            setSelectedTopics(parseResourceTopicsForSelect());
            setIsPublic(resource.isPublic);
            setSelectedOrgs(resource.ctOrgs ?? []);
        }
    }, [open, resource]);

    const filter = createFilterOptions();

    return(
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '150%' } }}
            maxWidth="md"
            fullWidth
            open={open}
            disableEnforceFocus={true}
        >
            <DialogTitle>{resource?.rsId > 0 ? 'Edit ' + resource.rsTitle : 'Add New Resource'}</DialogTitle>
            <DialogContent dividers>
                <Grid container>
                    <Grid item md={12}>
                        <FormLabel error={!!resourceTitleErrorMessage}>{resourceTitleErrorMessage ? resourceTitleErrorMessage : 'Title'}</FormLabel>
                        <br/>
                        <TextField
                            sx={{width: '100%'}}
                            value={resource?.rsTitle}
                            onChange={(event) => updateFieldByName(event, ResourceTitle)}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <FormLabel>Resource Description</FormLabel>
                        <br/>
                        <TextField
                            sx={{width: '100%'}}
                            value={resource?.rsShortDescription}
                            onChange={(event) => updateFieldByName(event, ResourceDescription)}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <FormLabel>Resource Type</FormLabel>
                        <br/>
                        <Autocomplete
                            freeSolo
                            value={resource?.resourceType}
                            onChange={(event, newValue) => {
                                changeResourceType(newValue);
                            }}
                            filterOptions={(options, params) => {
                                const filtered = filter(options, params);

                                const { inputValue } = params;
                                const isExisting = options.some((option) => inputValue.toLowerCase() === option.toLowerCase());
                                if(inputValue !== '' && !isExisting) {
                                    filtered.push(`Add "${inputValue}"`);
                                }

                                return filtered;
                            }}
                            inputValue={resourceTypeInputValue}
                            onInputChange={(event, newInputValue) => {
                                setResourceTypeInputValue(newInputValue);
                            }}
                            options={types}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4 }}>
                        <FormLabel>Resource Topics</FormLabel>
                        <br/>
                        <Autocomplete
                            freeSolo
                            multiple
                            renderInput={(params) => <TextField {...params} />}
                            options={topics.map((topic) => topic.name)}
                            renderValue={(value, getItemProps) =>
                                value.map((option, index) => {
                                    const { key, ...itemProps } = getItemProps({ index });
                                    return(<Chip variant="outlined" key={key} label={option} {...itemProps} />);
                                })
                            }
                            onChange={(event, newValue) => {
                                changeResourceTopics(newValue);
                            }}
                            filterOptions={(options, params) => {
                                const filtered = filter(options, params);
                                const { inputValue } = params;

                                const isExisting = options.some((option) => inputValue.toLowerCase() === option.toLowerCase());
                                if(inputValue !== '' && !isExisting) {
                                    filtered.push(`Add "${inputValue}"`);
                                }

                                return filtered;
                            }}
                            value={selectedTopics}
                        />
                    </Grid>
                    <Grid item md={12} sx={{
                        marginTop: '4px',
                        ".ck-editor__editable_inline": {
                            height: "200px",
                            border: (theme) =>
                                theme.palette.mode === "light"
                                    ? "1px solid rgba(0, 0, 0, 0.20) !important"
                                    : "1px solid rgba(200, 200, 200, 0.25) !important",
                            borderTop: "1px !important",
                        },
                    }}>
                        <FormControlLabel sx={{ display: 'block' }} control={<Checkbox checked={isPublic} onChange={() => changePublicVisibility()}/>} label="Visible to all Organizations" />
                        <Grid item md={12} sx={{marginTop: 4 }}>
                            <FormLabel>Allowed Organizations</FormLabel>
                            <br/>
                            <Autocomplete
                                disabled={isPublic}
                                multiple
                                renderInput={(params) => <TextField {...params} />}
                                options={selectableOrgs}
                                getOptionLabel={(option) => option.ogName }
                                isOptionEqualToValue={(option, value) => option?.ogId != null && value?.ogId != null && option.ogId === value.ogId}
                                renderTags={(value, getTagProps) =>
                                    !isPublic &&
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.ogId}
                                            label={option.ogName}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                onChange={(event, newValue) => {
                                    changeAllowedOrgs(newValue);
                                }}
                                filterOptions={(options, params) => {
                                    return filter(options, params);
                                }}
                                value={selectedOrgs}
                            />
                        </Grid>
                        <FormLabel>Resource Text</FormLabel>
                        {inputGlobalStyles}
                        <CKEditor
                            editor={ClassicEditor}
                            style={{ height: '400px' }}
                            config={{
                                toolbar: {
                                    items: [ 'heading',
                                        '|',
                                        'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                                        '|',
                                        'bold', 'italic', 'link', 'bulletedList', 'numberedList',
                                        '|',
                                        'blockQuote', 'insertTable', 'mediaEmbed', 'codeBlock',
                                        '|',
                                        'todoList', 'outdent', 'indent', 'undo', 'redo' ]
                                },
                                plugins: [
                                    Bold, Essentials, Italic, Mention, Paragraph, Undo, Link, List, Heading, Indent, MediaEmbed, Table, FontFamily, FontSize, BlockQuote, AutoLink, LinkEditing, LinkUI
                                ],
                                link: {
                                    addTargetToExternalLinks: true,
                                },
                                mediaEmbed: {
                                    previewsInData: true,

                                }
                            }}
                            data={resource?.rsContent}
                            onChange={(event, editor) => {
                                updateResourceContent(editor.getData());
                            }}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <FormLabel error={!!resourceImageErrorMessage}>{resourceImageErrorMessage ? resourceImageErrorMessage : 'Image'}</FormLabel>
                        <br/>
                        <Button variant={'contained'} onClick={() => {setShowAttachmentModal(true); setAttachmentType(resourceConstants.ImageAttachment)}}>{resource?.resourceImage ? 'Change' : 'Select'}</Button>
                        <CardMedia
                            component={'img'}
                            sx={{maxHeight: 200,padding: "1em 1em 0 1em", objectFit: 'contain', marginBottom: '10px'}}
                            image={resource?.resourceImage?.url}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <Grid container>
                            <Grid item md={12} sx={{marginTop: 4}}>
                                <FormLabel>Attachment</FormLabel>
                            </Grid>
                            <Grid item md={12}>
                                <Button
                                    variant={'contained'}
                                    onClick={() => {
                                        setShowAttachmentModal(true);
                                        setAttachmentType(resourceConstants.FileAttachment)
                                    }}
                                >
                                    {resource?.resourceAttachment ? 'Change' : 'Select'}
                                </Button>
                                {resource?.resourceAttachment &&
                                    <Button
                                        variant={'contained'}
                                        onClick={() => {
                                            setShowConfirmDeleteModal(true);
                                        }}
                                        sx={{ marginLeft: '5px'}}
                                        color={'error'}
                                    >
                                        Delete Attachment
                                    </Button>
                                }

                            </Grid>
                            <Grid item md={12}>
                                <FormLabel>{resource?.resourceAttachment?.fileName}</FormLabel>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <DialogActions>
                    <Button variant={'contained'} color={'error'} onClick={cancel}>Cancel</Button>
                    <Button variant={'contained'} onClick={saveResource}>Save</Button>
                </DialogActions>
            </DialogContent>
            <FileUploadModal onClose={handleFileUpload} open={showAttachmentModal} imageButton={true} />
            <ConfirmDeleteDialog
                onClose={(confirmed) => deleteAttachment(confirmed)}
                open={showConfirmDeleteModal}
                title={'Delete Attachment'}
                content={'Are you sure you want to delete this attachment?'}
            />
        </Dialog>
    )
}

EditResourceModal.propTypes = {
    resource: PropTypes.objectOf(ResourceDto),
    setResource: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    setSnackbarProperties: PropTypes.func,
    types: PropTypes.arrayOf(PropTypes.string),
    topics: PropTypes.arrayOf(ResourceTopicDto),
    cancel: PropTypes.func.isRequired,
    resourceApiService: PropTypes.objectOf(ResourceApiService).isRequired,
}
